import { ThirdPartyDraggable } from '@fullcalendar/interaction';
import React, { useEffect } from 'react';
import { halfHour } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { ExtendedProps } from 'shared/lib/full-calendar/event-allow';
import { DataType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export const useFullCalendarDraggableTasks = (
  tasks: Task[],
  containerRef: React.RefObject<HTMLElement>,
) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const today = useToday();

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Initialize ThirdPartyDraggable to connect @dnd-kit with Fullcalendar
    const thirdPartyDraggable = new ThirdPartyDraggable(containerRef.current, {
      // eslint-disable-next-line max-len -- long selector
      itemSelector: `[data-component-type="${DataType.Task}"], [data-component-type="${DataType.Goal}"], [data-component-type="${DataType.LifeArea}"]`,
      eventData: (el) => {
        const id = el.getAttribute('data-id');
        const task = tasks.find((task) => task.id === id);
        const title = el.getAttribute('data-title');
        const duration = el.getAttribute('data-duration') ?? halfHour;
        const componentType = el.getAttribute(
          'data-component-type',
        ) as DataType; // Cast to DataType

        if (!id) {
          return;
        }

        // Prepare extendedProps based on the component type
        const extendedProps: ExtendedProps = {
          weekStartsOn,
          task,
          originDate: today,
        };

        if (componentType === DataType.Goal) {
          extendedProps.goalId = id;
        }
        if (componentType === DataType.Task) {
          extendedProps.taskId = id;
        }
        if (componentType === DataType.LifeArea) {
          extendedProps.lifeAreaId = id;
        }

        return {
          id,
          title,
          extendedProps,
          duration: { ms: duration },
          create: true,
        };
      },
    });

    return () => thirdPartyDraggable.destroy();
  }, [containerRef, tasks, today, weekStartsOn]);
};
